<div fxLayout="column" fxLayoutAlign="center space-around" class="electronic-communication">
    <h1>Electronic Communication Consent</h1>
    <h4 class="date-updated mulish-bold"> Evolve Bank & Trust </h4>
    <h4 class="date-updated">IMPORTANT: PLEASE READ CAREFULLY</h4>

    <section class="section-separator">
        <article>
            <p>
                We want to provide you with communications electronically. Certain laws and regulations require us to provide communications to you “in writing,” which means you may be entitled to receive the information on paper. The E-SIGN Act allows us to provide you communications electronically and to conduct transactions with you electronically, with your consent. Please read this Evolve Bank & Trust Electronic Communication Consent (the “Consent”) carefully prior to providing us with your consent.
            </p>
            <p>    
                This Consent describes how we deliver and receive communications to and from you electronically and asks you to consent to use electronic records and signatures in our relationship with you. If you do not agree to this Consent or you later withdraw your consent provided herein, you may not be able to continue to use our services.
            </p>
        </article>
    </section>

    <section class="section-separator">
        <h2> Definitions </h2>
        <article>
            <p>
                “We”, “us”, and “our” means Evolve Bank & Trust, its current or future affiliated companies, agents, assignees and service providers. 
            </p>
            <p>
                “You” and “your” mean each applicant, account owner and anyone else with access to the account. If there is more than one owner, then these words mean each account owner separately, and all account owners jointly.
            </p>
            <p>
                “Communication” means each application, agreement, disclosure, notice, fee schedule, response to claims, statement, privacy policy, record, document and other information related to your account or to any Product, or that you sign, submit or agree to at our request.
            </p>
            <p>
                “Product” means each and every account, product or service we offer or will offer. The words “include” and “including,” when used at the beginning of a list of one or more items, indicates that the list contains examples and is not exclusive or exhaustive, and the items in the list are only illustrations.
            </p>
        </article>
    </section>

    <section class="section-separator">
        <h2> Scope of Communications to be Provided in Electronic Form. </h2>
        <article>
            <p>
                This Consent applies to all Communications and Products. By applying for or using a Product, you agree that any Communications will be provided in electronic format, to the extent allowed by law, and that paper Communications will not be sent. Your consent to receive electronic Communications and transactions includes, but is not limited to:
            </p>
            <ul>
                <li> All legal and regulatory disclosures and communications associated with the Product; </li>
                <li> The Account Terms, including any amendments thereto, and any and all agreements by and between you and us that relate to a Product; </li>
                <li> Privacy policies and notices; </li>
                <li> Responses to claims filed in connection with a Product; </li>
                <li> Statements; and </li>
                <li> All other communications between us and your concerning the Product and any related transactions, products or services. </li>
            </ul>
            <p> 
                Sometimes the law, or our agreement with you, requires you to give us a written notice. You must still provide these notices to us on paper, unless we specifically tell you in another Communication how you may deliver that notice to us electronically.
            </p>
            <p>
                We reserve the right, at our sole discretion, to discontinue the provision of electronic Communications, or to terminate or change the terms and conditions upon which electronic Communications are provided. We will provide you with notice of any such termination or change as required by law.
            </p>
        </article>
    </section>

    <section class="section-separator">
        <h2> Method of Providing Communications in Electronic Form. </h2>
        <article>
            <p>
                All Communications that we provide in electronic form will be provided either (1) by e-mail or (2) by access to a website designated in an email notice from us, (3) through the any mobile application we may make available, (4) to the extent permitted by law, by access to a website generally designated in advance for such purpose, or (5) in the manner specified in any other agreement we or our affiliates have with you.
            </p>
            <p>    
                If you seek to obtain a new product, service or account with us, we may remind you that you have already consented to receiving electronic Communications and using electronic signatures in your relationship with us.
            </p>
            <p>
                Continuing to use our Products after receiving updates to our system requirements signifies your acceptance of the change and reaffirmation of your consent.
            </p>
        </article>
    </section>

    <section class="section-separator">
        <h2> Keeping your Records Current. </h2>
        <article>
            <p>
                It is your responsibility to provide us with a true, accurate and complete e-mail address, street address, and other information related to this Consent and a Product, and to maintain and update promptly any changes in this information. You can update information (such as your e-mail address) by contacting us at <a href="mailto:support@awsmbank.com"> <u>support@awsmbank.com</u></a>. We are not responsible for any delay or failure in the receipt of the Communications if we send the Communications to the last e-mail address you provided to us.
            </p>
        </article>
    </section>

    <section class="section-separator">
        <h2> System Requirements for Accessing Communications. </h2>
        <article>
            <p>
                In order to access, view, and retain electronic Communications that we make available, you must have:
            </p>
            <ul>
                <li> an Apple iPhone or iPad running iOS version 12.0 or higher. </li>
                OR
                <li> an Android phone running Android version 9.0 or higher. </li>
                <li> access to an active e-mail account with an email service provider. </li>
            </ul>
            <p> 
                We may update these requirements as necessary to preserve the ability to receive electronic Communications. If there is a substantial change in these requirements, you will be notified as required by law.
            </p>
        </article>
    </section>


    <section class="section-separator">
        <h2> Requesting Paper Copies. </h2>
        <article>
            <p>
                We will not send paper copies of any Communication; however, we reserve the right, but assume no obligation, to provide a paper (instead of electronic) copy of any Communication that you have authorized us to provide electronically. You can obtain a paper copy of an electronic Communication by printing it or by requesting that we mail a paper copy. To request a paper copy, email us at <a href="mailto:support@awsmbank.com"> <u>support@awsmbank.com</u></a>. There may be a fee associated with the request for the delivery of paper copies of any Communication provided electronically pursuant to this Consent.
            </p>
        </article>
    </section>

    <section class="section-separator">
        <h2> Communications in Writing. </h2>
        <article>
            <p>
                All Communications in either electronic or paper format from us to you will be considered “in writing.” You should print or download a copy of this Consent and any other Communications for your records.
            </p>
        </article>
    </section>

    <section class="section-separator">
        <h2> Withdrawing Your Consent </h2>
        <article>
            <p>
                You can withdraw your consent to receive Communications electronically at any time. Your withdrawal of consent will become effective after we have had a reasonable opportunity to act upon it. To withdraw your consent to receive Communications electronically, you must contact us by e-mailing at <a href="mailto:support@awsmbank.com"> <u>support@awsmbank.com</u> </a>.
            </p>
            <p>
                If you withdraw consent, your access to all Products will be terminated and any account you have with us will be closed and funds will be returned to you in accordance with the account agreement. If you withdraw consent, the legal validity and enforceability of prior Communications delivered in electronic form will not be affected.
            </p>
        </article>
    </section>

    <section class="section-separator">
        <h2> Consent </h2>
        <article>
            <p>
                By applying for or using a Product or by checking any call to action (including “I Agree” or similar language), you give us affirmative consent to receive electronic Communications as described herein.
            </p>
            <p>
                By providing your consent, you are also confirming that you have the hardware and software described above, that you are able to receive and review electronic Communications, and that you have an active email account. You are also confirming that you are authorized to, and do, consent on behalf of all the other account owners, authorized signers, authorized representatives, delegates, product owners and/or service users identified with your Products.
            </p>
        </article>
    </section>
</div>